// Every input[type="tel"] on the page is upgraded with the intl-tel-input
// country widget (all countries, AU/NZ ordered first). No opt-in markup is
// needed. Used by generic CMS forms: the framework renders type="tel" for 'tel'
// elements, that input submits, so the E.164 number is written back into it.
//
// The sign-up and my-details pages are deliberately NOT handled here: they keep
// the original AU/NZ-only widget and their own inline validation, set up by
// ./register-phone.js. Their inputs carry data-phone-au-nz, which this module
// skips.
//
// Optional per-input hooks, for any future page that wants them:
//   data-phone-target="<id>"  - hidden field that receives the E.164 number
//                               (the visible input then stays national)
//   data-phone-mobile-only    - reject landlines
//
// Pasting is handled explicitly because the library only gets half of it right:
// it detects the country from a pasted `+61...`, but it does NOT strip the dial
// code out of the input on an input event (_beforeSetNumber is only reached via
// setNumber() and init), which would leave `+61` showing twice. It also doesn't
// recognise the `0061` IDD form at all. So we intercept the paste, normalise a
// leading `00` to `+`, and hand the result to the public setNumber(), which does
// both the country detection and the dial-code strip.

import intlTelInput from 'intl-tel-input/intlTelInputWithUtils';
import { release as releaseSubmitLock } from './form-submit-lock';

// International access prefix, e.g. 0061412345678 -> +61412345678. No national
// number begins 00, so a leading 00 is unambiguously an IDD prefix.
const IDD_PREFIX = /^00(?=\d)/;

// National prefixes that imply a country while none is selected yet. The field
// starts with no country (globe icon), and most visitors type their mobile the
// way they say it - "04xx xxx xxx" - without touching the dropdown, which used
// to stop the submit with "Select a country code". 04 is the Australian mobile
// range, so pick AU for them. Only applied while no country is selected, so a
// deliberate choice is never overridden.
const NATIONAL_PREFIX_COUNTRY = [
	{ prefix: /^04/, iso2: 'au' },
];

// Inputs owned by this module. data-phone-au-nz marks the sign-up and my-details
// fields, which ./register-phone.js sets up instead.
const SELECTOR = 'input[type="tel"]:not([data-phone-au-nz])';

class FormPhone {
	static #initialized = false;
	static #instances = new WeakMap();

	static initialize() {
		if (this.#initialized) return;
		this.#initialized = true;

		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => this.#setup());
		} else {
			this.#setup();
		}
	}

	static #setup() {
		document.querySelectorAll(SELECTOR).forEach(input => {
			try {
				const instance = new FormPhone(input);
				FormPhone.#instances.set(input, instance);
				instance.#bind();
			} catch (error) {
				console.warn('FormPhone error:', error);
			}
		});

		// Validate and sync from a capture-phase listener on the document, which
		// runs before any submit handler bound to the form itself. ajax-form.js
		// binds $(form).submit() and posts $form.serialize(), and it is imported
		// ahead of this module - so a listener on the form would register second
		// and serialize() would read the field before the E.164 number was written.
		//
		// An invalid number blocks the submit and is reported through the
		// browser's native validation bubble, so generic CMS forms get feedback
		// without any extra markup. The sign-up and my-details pages never reach
		// this path - they run their own validation and post over ajax.
		document.addEventListener('submit', event => {
			let blocked = false;

			document.querySelectorAll(SELECTOR).forEach(input => {
				if (input.form !== event.target) return;

				const instance = FormPhone.for(input);
				if (!instance) return;

				// validate() syncs on success, so no separate sync() call here.
				const error = instance.validate();

				if (error && !blocked) {
					blocked = true;
					input.setCustomValidity(error);
					input.reportValidity();
				}
			});

			if (blocked) {
				event.preventDefault();
				event.stopImmediatePropagation();

				// form-submit-lock.js saw this submit first (its listener registers
				// at module load, ours on DOMContentLoaded) and decides what to do
				// with the button a tick later. Because stopImmediatePropagation()
				// keeps the form's invisible-reCAPTCHA handler from running, that
				// module sees "cancelled, no request, captcha present" and holds
				// the button disabled for its captcha leash (2 minutes). No captcha
				// is actually running, so hand the button back. The extra tick puts
				// this after the lock module's own deferred decision; on forms
				// where it already undid the lock itself this is a no-op.
				setTimeout(() => releaseSubmitLock(event.target), 0);
			}
		}, true);
	}

	static for(input) {
		return input ? FormPhone.#instances.get(input) : undefined;
	}

	#input;
	#iti;
	#target;
	#mobileOnly;

	constructor(input) {
		this.#input = input;
		// Element that receives the E.164 number. Without one the input itself
		// is the field that submits, so it takes the number.
		this.#target = document.getElementById(input.dataset.phoneTarget ?? '') ?? null;
		this.#mobileOnly = input.hasAttribute('data-phone-mobile-only');

		this.#iti = intlTelInput(input, {
			countryOrder: ['au', 'nz'],
			// No initialCountry, as on the original implementation: the field starts
			// with no country selected (globe icon) and the user picks one, or a
			// pasted +XX number selects it. validate() reports the unselected case.
			separateDialCode: true,
			nationalMode: true,
			strictMode: true,
			// strictMode caps input at the longest valid number of these types,
			// and since v25 isValidNumberPrecise() enforces them too. The default
			// is ['MOBILE'], which would reject valid landlines on generic forms,
			// so null (any type) unless the input opts into mobile-only.
			validationNumberTypes: this.#mobileOnly ? ['MOBILE'] : null,
			useFullscreenPopup: false,
		});
	}

	get iti() {
		return this.#iti;
	}

	#bind() {
		this.#input.addEventListener('paste', event => this.#handlePaste(event));
		this.#input.addEventListener('change', () => this.#normalise());
		// Clear a submit-time error as soon as the user changes anything, so the
		// browser's own pre-submit check does not keep blocking the next attempt
		// with stale text. Picking a country from the dropdown fires the plugin's
		// countrychange event on the input rather than an input event - without
		// this, an autofilled number that was rejected for "select a country"
		// stays rejected after the country is chosen.
		const clearError = () => this.#input.setCustomValidity('');
		this.#input.addEventListener('input', clearError);
		this.#input.addEventListener('change', clearError);
		this.#input.addEventListener('countrychange', clearError);
		// Pick the country from a national prefix as the user types (and on
		// autofill, which fires change). Runs after clearError so the field is
		// in a clean state when the country flips.
		this.#input.addEventListener('input', () => this.#inferCountry());
		this.#input.addEventListener('change', () => this.#inferCountry());
		// Submit is handled once, in #setup(). The my-details page never fires a
		// submit event at all - it serialises the form and posts over ajax -
		// which is why sync() is public and called directly there.
	}

	// Route every paste through setNumber() so a pasted dial code both sets the
	// country and is stripped out of the field. This replaces the whole value
	// rather than inserting at the caret, which is what you want for a phone
	// number.
	#handlePaste(event) {
		const pasted = event.clipboardData?.getData('text');
		if (!pasted) return;

		event.preventDefault();
		this.#setNumber(pasted);
	}

	// Safety net for a value that arrives without a paste event - browser
	// autofill being the common one. Only acts on something that still carries
	// an international prefix, so normal national input is left untouched.
	#normalise() {
		const value = this.#input.value;
		if (value.trim().startsWith('+') || IDD_PREFIX.test(value.replace(/\D/g, ''))) {
			this.#setNumber(value);
		}
	}

	#setNumber(value) {
		const digits = value.replace(/\D/g, '');

		// setNumber() does not fire an input event, so clear any submit-time
		// error here as well - otherwise the browser's own pre-submit check
		// would keep blocking on stale text.
		this.#input.setCustomValidity('');

		if (value.trim().startsWith('+')) {
			this.#iti.setNumber(`+${digits}`);
		} else if (IDD_PREFIX.test(digits)) {
			this.#iti.setNumber(digits.replace(IDD_PREFIX, '+'));
		} else {
			this.#iti.setNumber(digits);
			// A pasted national number carries no dial code for the library to
			// detect the country from, so fall back to the prefix table.
			this.#inferCountry();
		}
	}

	// While no country is selected, choose one from a recognised national
	// prefix (see NATIONAL_PREFIX_COUNTRY). setCountry() keeps the typed digits
	// as the national number, so "0412 345 678" becomes +61412345678 on sync.
	#inferCountry() {
		if (this.#iti.getSelectedCountryData().iso2) {
			return;
		}

		const digits = this.#input.value.replace(/\D/g, '');

		if (!digits || IDD_PREFIX.test(digits)) {
			return;
		}

		const match = NATIONAL_PREFIX_COUNTRY.find(({ prefix }) => prefix.test(digits));

		if (match) {
			this.#iti.setCountry(match.iso2);
		}
	}

	// Write the full E.164 number where the form expects it. separateDialCode
	// leaves only the national part in the visible input, so without this the
	// country the user picked would be thrown away.
	sync() {
		const international = this.#iti.getNumber();

		if (this.#target) {
			// The visible input stays national for display; the hidden field
			// carries the number. Empty clears it, so deleting your number saves.
			this.#target.value = this.#input.value.trim() ? international : '';
		} else if (international) {
			// No separate target, so the input itself submits. Left alone when
			// empty or unparseable rather than wiped on the way out.
			this.#input.value = international;
		}
	}

	// Returns an error message, or '' when the number is acceptable. The field
	// is optional everywhere it is used, so empty is always acceptable.
	validate() {
		if (!this.#input.value.trim()) {
			this.sync();
			return '';
		}

		// Without libphonenumber there is nothing to validate against. Let it
		// through rather than blocking someone from finishing the form.
		if (!intlTelInput.utils) return '';

		// Last chance to pick the country from the number itself before the
		// "select a country" error - covers a value set without input/change
		// events (some autofill paths, scripted values).
		this.#inferCountry();

		if (!this.#iti.getSelectedCountryData().iso2) {
			return 'Select a country code to enter a mobile number';
		}

		const noun = this.#mobileOnly ? 'mobile number' : 'phone number';

		// With validationNumberTypes set to MOBILE, isValidNumberPrecise() below
		// already fails a landline - but with a generic message. Name the real
		// problem first, for any number whose type libphonenumber can identify.
		// A length check cannot separate the two - AU mobiles and landlines are
		// both 9 national digits - so the type has to be inspected directly.
		if (this.#mobileOnly) {
			const { numberType } = intlTelInput.utils;
			const type = this.#iti.getNumberType();

			// NANP numbers report FIXED_LINE_OR_MOBILE; there is no way to tell.
			if (type !== numberType.UNKNOWN
				&& type !== numberType.MOBILE
				&& type !== numberType.FIXED_LINE_OR_MOBILE) {
				return 'Enter a mobile number, not a landline';
			}
		}

		if (!this.#iti.isValidNumberPrecise()) {
			const { validationError } = intlTelInput.utils;

			switch (this.#iti.getValidationError()) {
				case validationError.TOO_SHORT:
					return `That ${noun} is too short`;
				case validationError.TOO_LONG:
					return `That ${noun} is too long`;
				case validationError.INVALID_COUNTRY_CODE:
					return 'Select a valid country code';
				default:
					return `Enter a valid ${noun}`;
			}
		}

		this.sync();

		return '';
	}
}

FormPhone.initialize();

// Exposed for any inline page script that does its own validation / ajax
// submit. Not currently used by the member pages, which run their own checks.
window.formPhone = {
	validate: input => FormPhone.for(input)?.validate() ?? '',
	sync: input => FormPhone.for(input)?.sync(),
	get: input => FormPhone.for(input)?.iti,
};

export default FormPhone;
